import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import { distanceBetweenPoses } from '@sb/geometry';
import type { DeviceCommand } from '@sb/integrations/types';
import { info, namespace, warn } from '@sb/log';
import type {
  ArmTarget,
  StepTooltip,
  TCPFrame,
  TCPOffsetOption,
} from '@sb/motion-planning';
import {
  DEFAULT_CONSTANT_SPEED_TOOLTIP_ACCELERATION,
  DEFAULT_TCP_OFFSET_OPTION,
  getEffectiveStepTooltip,
  namesNoTooltip,
  resolveStepTooltip,
} from '@sb/motion-planning';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { SpeedProfile } from '@sb/routine-runner/speed-profile';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { wait, yieldToEventLoop } from '@sb/utilities';
import type { NonEmptyArray } from '@sb/utilities/src/types';

import type { PushModeStep } from '..';
import { STILL_GOING_CHECK_INTERVAL_MS } from '../../constants';
import type { RoutineContext } from '../../RoutineContext';
import type { Conditional, Expression, StepFailure } from '../../types';
import { resolveTCPFrame } from '../../util/tcpFrame';
import type ArcStep from '../Arc/Step';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';
import type WaypointStep from '../Waypoint/Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

const ns = namespace('MoveArmToStepV2');

/**
 * The tooltips a whole play cycle resolves against.
 *
 * Settled once per cycle rather than per `doPlay` entry: a constant-speed move
 * is a single continuous trajectory, and `doPlay` re-enters once per waypoint
 * segment — after a stop-early condition, and after the constant-speed preflight
 * pause. Re-reading the Active Tooltip on each entry would let a write that
 * lands between two segments re-frame the segments that remain while the arm is
 * midway through the move.
 */
interface PlayCycleTooltips {
  /**
   * The robot's Active Tooltip. What a child's `tcpOption: 'active'` resolves to,
   * and what a Move step naming no tooltip of its own falls back to. Absent
   * where the robot has none, which reads as the wrist flange.
   */
  activeTooltip: TCPFrame | undefined;
  /**
   * The Move step's own tooltip, reconciled across the two fields it can be
   * stored in. What a child's `tcpOption: 'parent'` resolves through, and the
   * source of the forced frame below — the same tooltip in both roles, so a
   * child cannot inherit one tooltip while the move commands another.
   */
  stepTooltip: StepTooltip;
  /**
   * The frame every segment of a constant-speed move reads and commands in
   * (mirrors Weld). `undefined` when constant speed is off, where the Move
   * step's tooltip is inherited by choice rather than forced.
   */
  constantSpeedForcedFrame: TCPFrame | undefined;
}

export default class MoveArmToV2Step extends Step<Arguments, Variables> {
  private static STOP_EARLY_CHECK_INTERVAL_MS_FAST = 5;

  private static STOP_EARLY_CHECK_INTERVAL_MS_DEFAULT = 25;

  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  private currentWaypointIndex: number = 0;

  /**
   * The tooltip pose when the current waypoint started being processed.
   * Updated for each waypoint as it begins processing in the waypoint loop.
   */
  private currentWaypointStartingPose: CartesianPose | null = null;

  // whether the step is done either by finish motioning, erroring, or being cancelled
  private stepDone: boolean = false;

  private paused: boolean = false;

  private lastPauseLogTime: number = 0;

  private removeStopEarlyMonitoring: ReturnType<typeof setInterval> | null =
    null;

  private distanceMonitoringHandle: (() => void) | null = null;

  // ensure we only run pre-motion non-motion substeps once per play cycle
  private preMotionSubstepsExecuted: boolean = false;

  // Resolved on the first `doPlay` of a play cycle and held for the rest of it.
  private playCycleTooltips: PlayCycleTooltips | null = null;

  /**
   * The option this play's constant-speed pin wrote to the global motion
   * frame, recorded so the end-of-play restore can tell its own write from
   * anyone else's. Recorded at the write site rather than derived from the
   * arguments: the pin resolves a step's tooltip through the Active Tooltip,
   * so the value written is not always `args.constantSpeedTCPOption`.
   */
  private pinnedTCPOption?: TCPOffsetOption;

  // Device actuation (lift) state mirroring MoveVerticalLift behavior
  private deviceActuationInProgress: boolean = false;

  private deviceActuationPaused: boolean = false;

  // Timestamp when we programmatically paused the arm (to keep it still for lift)
  // Used to ignore only the immediate 'pause' event caused by ourselves, not user pauses
  private lastSelfPauseAtMs: number | null = null;

  // Store pauseRoutine callback for use when stopping early
  private pauseRoutineCallback?: (details: any) => void;

  // Store fail callback for use in async error handling
  private failCallback?: (failure: StepFailure) => Promise<void>;

  public initializeVariableState(): void {
    const { completedCount = 0, distanceMovedToNextWaypoint = 0 } =
      this.variablesForInitialization;

    this.variables = {
      completedCount,
      currentActivity: 'none',
      distanceMovedToNextWaypoint,
      currentWaypointID: null,
    };
  }

  private motion?: ReturnType<RoutineContext['doMotion']>;

  private motionApproach?: ReturnType<RoutineContext['doMotion']>;

  private motionPromise?: Promise<any>;

  // Pause gate for blocking after approach until user resumes (preflight).
  // Mirrors the Weld step's createPauseGate/waitForResume pattern.
  private pauseGate: {
    promise: Promise<'resume' | 'stop'>;
    resolve: (kind: 'resume' | 'stop') => void;
  } | null = null;

  public async _play(playArgs: StepPlayArguments): Promise<void> {
    info(ns`_play`, `playing MoveArmToV2Step`);

    this.paused = false;
    this.currentWaypointIndex = 0;
    this.stepDone = false;
    this.preMotionSubstepsExecuted = false;
    this.playCycleTooltips = null;
    this.pinnedTCPOption = undefined;
    this.publishActiveWaypoint();

    // Constant speed pins the global motion frame to the step's tooltip for
    // its target math (see doPlay). The pin is scoped to this step: the
    // operator's frame is captured here and put back on exit, so a later
    // step that resolves `'auto'` against the register (a relative-to-current
    // waypoint) reads the operator's frame rather than inheriting this
    // step's tooltip.
    const preStepTCPOption =
      this.routineContext.getRoutineRunnerState().kinematicState
        .tcpOffsetOption;

    try {
      while (!this.stepDone) {
        if (this.paused) {
          if (Date.now() - this.lastPauseLogTime > 1000) {
            this.lastPauseLogTime = Date.now();

            info(ns`doPlay`, `paused, waiting for resume`);
          }

          await wait(STILL_GOING_CHECK_INTERVAL_MS);

          continue;
        }

        await this.doPlay(playArgs);
      }
    } finally {
      this.restorePreStepTCPOption(preStepTCPOption);
    }

    this.setVariable('currentWaypointID', null);
  }

  /**
   * Put the global motion frame back where it was before this step's
   * constant-speed pin. Restores only this step's own write: a register
   * moved by someone else mid-step (an operator jog under pause, a WebSocket
   * `setTCPOffsetOption`) keeps their choice, and a play that never pinned
   * (`pinnedTCPOption` unset) restores nothing.
   */
  private restorePreStepTCPOption(preStepTCPOption: TCPOffsetOption): void {
    const { tcpOffsetOption } =
      this.routineContext.getRoutineRunnerState().kinematicState;

    if (
      this.pinnedTCPOption != null &&
      tcpOffsetOption === this.pinnedTCPOption &&
      tcpOffsetOption !== preStepTCPOption
    ) {
      this.routineContext.setTCPOffsetOption(preStepTCPOption);
    }
  }

  private async doPlay(playArgs: StepPlayArguments): Promise<void> {
    const { fail, pushMode, hasEncounteredStartStep, pauseRoutine } = playArgs;

    // Store pauseRoutine callback for use when stopping early
    this.pauseRoutineCallback = pauseRoutine;
    // Store fail callback for use in async error handling
    this.failCallback = fail;

    if (!hasEncounteredStartStep()) {
      // If we haven't encountered the start step yet, we don't need to play
      info(
        ns`doPlay`,
        `Skipping MoveArmToV2Step because we haven't reached the start step`,
      );

      return;
    }

    if (this.variables.currentActivity !== 'none') {
      // cannot play unless the step is doing nothing currently
      throw new Error(
        `cannot double play MoveArmToV2Step (current activity: ${this.variables.currentActivity})`,
      );
    }

    // A step naming no tooltip at all resolves to the Active Tooltip only where
    // the Move step's Tooltip row exists to say so. Without that row there is no
    // way to author a tooltip, so an unset constant-speed step is the
    // misconfiguration it has always been and fails as loudly as it always has.
    if (
      this.args.enforceConstantSpeed &&
      namesNoTooltip(this.args) &&
      !getFeatureFlagSync('setActiveTooltip', false)
    ) {
      await fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason:
          'Constant speed requires a specific tooltip to be selected. Please select a tooltip in the constant speed settings.',
        error: new Error('constantSpeedTCPOption is not set') as any,
      });

      return;
    }

    this.playCycleTooltips ??= this.resolvePlayCycleTooltips();

    const { activeTooltip, stepTooltip, constantSpeedForcedFrame } =
      this.playCycleTooltips;

    // Set the TCP offset option early — before targets are collected —
    // so that getTargetPose and subtractEquipmentOffset use the correct
    // TCP frame. Without this, the first execution uses the default TCP
    // (often 'wrist'), producing targets in the wrong frame.
    //
    // Only the option NAME is pinned globally: the global motion frame stays
    // name-only (phase-2 model, where `writeTcpFrame` pairs it with the Active
    // Tooltip's id when the names match). The `gripperID` half rides on the
    // per-waypoint forced frame below, where an `ee-*` name shared by two
    // equipment rows still has to name one of them.
    if (constantSpeedForcedFrame) {
      this.pinnedTCPOption = constantSpeedForcedFrame.tcpOption;

      this.routineContext.setTCPOffsetOption(
        constantSpeedForcedFrame.tcpOption,
      );
    }

    let targets: ArmTarget[] = [];

    // The motion substep each entry of `targets` came from (a substep can
    // expand to several targets). Used to name the failing waypoint when
    // planning fails; must be sliced in lock-step with `targets`.
    let targetSubsteps: Array<WaypointStep | ArcStep> = [];

    let matchDynamicBasePositionArray: Array<CartesianPose | undefined> = [];

    let speedProfiles: SpeedProfile[] = [];
    let totalCommonScalings: number[] = [];

    const { kinematicState } = this.routineContext.getRoutineRunnerState();

    let previousTargetPose: CartesianPose =
      kinematicState.tcpOffsetOption === 'wrist'
        ? kinematicState.wristPose
        : kinematicState.tooltipPoint;

    // Read the matched read/write-frame flag ONCE, at step start: all the
    // plumbing below is gated on it, so OFF is byte-identical to today.
    const matchFramesEnabled = getFeatureFlagSync(
      'matchRelativeToCurrentFrames',
      true,
    );

    // The active global TCP an `auto` current-position waypoint commands in.
    // Captured ONCE so an async TCP writer can't change a later waypoint's tool.
    const capturedGlobalTCPOption: TCPOffsetOption | undefined =
      matchFramesEnabled ? kinematicState.tcpOffsetOption : undefined;

    // Integrator tag: the frame `previousTargetPose` is genuinely expressed in.
    // The seed pose is `wristPose` (the flange, always wrist-framed) when the
    // active frame is 'wrist', else `tooltipPoint`. `setTCPOffsetOption` keeps
    // `tooltipPoint` consistent with `tcpOffsetOption` and `tooltipGripperID`,
    // so the seed's frame is the live tag and its device — including after
    // constant-speed's in-step switch above, which re-expresses `tooltipPoint`
    // into the new frame.
    const seedFrame: TCPOffsetOption = kinematicState.tcpOffsetOption;

    let previousTargetTCPFrame: TCPFrame | undefined = matchFramesEnabled
      ? // The device can be a disabled one. Leaving it off would let a by-name
        // read resolve the tip against enabled equipment and skip the
        // conversion.
        { tcpOption: seedFrame, gripperID: kinematicState.tooltipGripperID }
      : undefined;

    const motionSubsteps = this.getActiveMotionSubsteps();

    info(
      ns`doPlay`,
      `adding steps from waypoint index ${this.currentWaypointIndex} to ${motionSubsteps.length - 1}`,
    );

    // Before planning/motion, execute any non-motion substeps that appear
    // before the first motion substep (e.g., Set IO, Set Variable). These
    // can influence planning and should occur prior to requesting the plan.
    if (!this.preMotionSubstepsExecuted && this.currentWaypointIndex === 0) {
      const preMotionSubsteps =
        this.getActiveNonMotionSubstepsBeforeFirstMotion();

      if (preMotionSubsteps.length > 0) {
        info(
          ns`doPlay`,
          `executing ${preMotionSubsteps.length} pre-motion non-motion substeps`,
        );

        for (const step of preMotionSubsteps) {
          try {
            await step.play(playArgs);
          } catch (err) {
            await fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason:
                (err as Error)?.message ??
                'Failed executing pre-motion non-motion substep',
              error: err as any,
            });

            return;
          }
        }
      }

      this.preMotionSubstepsExecuted = true;
    }

    // Process waypoints first
    for (const substep of motionSubsteps.slice(this.currentWaypointIndex)) {
      const childStep = substep as WaypointStep | ArcStep;

      // Snapshot the predecessor (pose, frame) the main getArmTarget reads,
      // before the loop below reassigns them to this waypoint's commanded
      // values — the dynamic-base follow-up call must re-express from the same
      // frame as the main call (see below).
      const mainCallPreviousTargetPose = previousTargetPose;
      const mainCallPreviousTargetTCPFrame = previousTargetTCPFrame;

      // Both child kinds stamp the constant-speed forced frame inside
      // getArmTarget BEFORE computing the carry tag, so `resultTag` below is
      // truthful at source. On Waypoint it also pins the relative
      // read/write frame; Arc has no relative-to-current read.
      const result = await (this.isWaypointStep(childStep)
        ? childStep.getArmTarget({
            parentCompletedCount: this.variables.completedCount,
            previousTargetPose,
            capturedGlobalTCPOption,
            previousTargetTCPFrame,
            forcedTooltip: matchFramesEnabled
              ? constantSpeedForcedFrame
              : undefined,
            activeTooltip,
            parentTooltip: stepTooltip,
          })
        : childStep.getArmTarget({
            parentCompletedCount: this.variables.completedCount,
            previousTargetPose,
            forcedTooltip: matchFramesEnabled
              ? constantSpeedForcedFrame
              : undefined,
            activeTooltip,
            parentTooltip: stepTooltip,
          }));

      if ('failure' in result) {
        fail(result);

        return;
      }

      // target could be an array of targets of a single target, convert to an array so I can iterate over

      const maybeTargetArray = result.target;
      const { poseForPreviousTarget } = result;

      const resultTag = result.poseForPreviousTargetTCPFrame;

      const targetArray = Array.isArray(maybeTargetArray)
        ? maybeTargetArray
        : [maybeTargetArray];

      for (let target of targetArray) {
        previousTargetPose = poseForPreviousTarget;

        // When enforceConstantSpeed is enabled, convert joint targets to
        // pose targets so the Cartesian planner can process them.
        if (
          this.args.enforceConstantSpeed &&
          (!('pose' in target) || !target.pose)
        ) {
          try {
            const pose = await this.routineContext.getTargetPose(target);

            // Destructure out jointAngles so the motion planner uses the pose
            const { jointAngles: _removed, ...rest } = target as any;
            target = { ...rest, pose };
          } catch (err) {
            await fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason:
                (err as Error)?.message ??
                'Failed to convert target to pose for constant-speed planning',
              error: err as any,
            });

            return;
          }
        }

        // Apply blend configuration based on parent config
        if (
          'useParentBlendConfig' in childStep.args &&
          childStep.args.useParentBlendConfig
        ) {
          target.blendRadius = this.args.blendConfig.radius;
        }

        // Constant speed runs the whole move as one continuous trajectory, so
        // every corner must blend to hold a steady tooltip speed. Waypoints
        // default to "stop here" (useParentBlendConfig=false, radius 0), which
        // would otherwise zero out the blend at that corner and stop the arm —
        // making the Move step's constant-speed blend have no effect. Fall back
        // to the Move step's blend whenever the waypoint has no blend of its
        // own; a waypoint's own non-zero blend still overrides it here.
        if (this.args.enforceConstantSpeed && !target.blendRadius) {
          target.blendRadius = this.args.blendConfig.radius;
        }

        // Forces the Move step's TCP onto the commanded target (read by
        // subtractEquipmentOffset, as the `(effectiveTCPOption, gripperID)`
        // pair). On the matched-frames flag the children already stamped this
        // frame in getArmTarget, making this redundant; off the flag it is the
        // only forcing. Delete with the flag.
        if (constantSpeedForcedFrame) {
          (target as any).tcpOption = constantSpeedForcedFrame.tcpOption;

          (target as any).effectiveTCPOption =
            constantSpeedForcedFrame.tcpOption;

          // Only when the frame names its equipment outright; see the matching
          // branch in Waypoint/Step.ts.
          if (constantSpeedForcedFrame.gripperID != null) {
            (target as any).gripperID = constantSpeedForcedFrame.gripperID;
          }
        }

        // Carry the tag in lock-step with `previousTargetPose`. It MUST name
        // the frame the carry pose is GENUINELY in — not always the commanded
        // frame: a joint-match waypoint carries its stored teach-time pose in
        // its stored frame, and tagging that with the forced frame made the
        // next current-position waypoint dive by the tool-offset gap.
        if (matchFramesEnabled) {
          previousTargetTCPFrame = resultTag;
        }

        // Since per-waypoint speed profiles are not yet supported, we use the parent speed profile.
        speedProfiles.push(this.args.speedProfile);

        targets.push(target);
        targetSubsteps.push(childStep);

        totalCommonScalings.push(this.getScalingFromWptSpeedLimits(childStep));

        const shouldMatchDynamicBase =
          this.isWaypointStep(childStep) &&
          childStep.args.shouldMatchDynamicBasePosition;

        if (shouldMatchDynamicBase) {
          // forcePoseGeneration bypasses shouldMatchJointAngles. Re-express from
          // the same predecessor frame the main call used (the snapshot above),
          // not this waypoint's commanded frame. Off the flag: unchanged pose +
          // undefined tags.
          const tempResult = await childStep.getArmTarget({
            parentCompletedCount: this.variables.completedCount,
            previousTargetPose: matchFramesEnabled
              ? mainCallPreviousTargetPose
              : previousTargetPose,
            forcePoseGeneration: true, // This forces pose generation even when shouldMatchJointAngles is true
            capturedGlobalTCPOption,
            previousTargetTCPFrame: matchFramesEnabled
              ? mainCallPreviousTargetTCPFrame
              : previousTargetTCPFrame,
            forcedTooltip: matchFramesEnabled
              ? constantSpeedForcedFrame
              : undefined,
            activeTooltip,
            parentTooltip: stepTooltip,
          });

          if ('failure' in tempResult) {
            matchDynamicBasePositionArray.push(undefined);
          } else {
            const tempTarget = tempResult.target;

            const poseForDeviceCompensation =
              'pose' in tempTarget ? tempTarget.pose : undefined;

            matchDynamicBasePositionArray.push(poseForDeviceCompensation);
          }
        } else {
          // No device compensation needed
          matchDynamicBasePositionArray.push(undefined);
        }
      }
    }

    let hasPushed = false;

    if (pushMode) {
      info(ns`doPlay`, `checking push mode`);

      for (const parentStep of this.parentSteps) {
        if (parentStep.getStepKind() === 'PushMode') {
          const pushModeStep = parentStep as PushModeStep;

          if (pushModeStep.hasPushed) {
            hasPushed = true;
          }

          pushModeStep.hasPushed = true;
        }
      }
    }

    if (await this.checkStopEarlyConditions()) {
      info(
        ns`checkStopEarlyConditions`,
        'stop early condition met before motion, checking next waypoint',
      );

      // The checkStopEarlyConditions already incremented the waypoint index
      // Check if there are more waypoints, otherwise mark as done
      if (this.currentWaypointIndex >= this.getActiveMotionSubsteps().length) {
        this.stepDone = true;
      }

      // Return to let the outer loop in _play call doPlay again for the next waypoint
      return;
    }

    const currentPose: CartesianPose =
      this.routineContext.getRoutineRunnerState().kinematicState.tooltipPoint;

    this.currentWaypointStartingPose = currentPose;
    this.startDistanceMonitoring();
    this.startMonitoringStopEarly();

    try {
      await this.updateHexEForceMonitoring();
    } catch (err) {
      await fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason:
          (err as Error)?.message ??
          'Failed to arm force stop-early monitoring',
        error: err as any,
      });

      return;
    }

    // When constant speed is enabled do a normal joint move to the first
    // waypoint (approach) before cartesian planning the remaining waypoints.
    //
    // Only when that substep is a single target. An Arc expands to
    // `[via, end]`, and an approach carrying only the via would strand the
    // arc's end in the cartesian plan — a straight line where the arc's first
    // half belongs — and credit the whole Arc as reached at its midpoint. Left
    // whole, the tracker reports it like any other waypoint.
    const approachSubstep = targetSubsteps[0];

    const canApproachFirstTarget =
      approachSubstep != null && this.isWaypointStep(approachSubstep);

    if (this.args.enforceConstantSpeed && !canApproachFirstTarget) {
      info(
        ns`doPlay`,
        'constant speed: first motion substep is not a single-target waypoint, planning it with the cartesian segment instead of approaching it',
      );
    }

    if (this.args.enforceConstantSpeed && canApproachFirstTarget) {
      const approachCompleted = await this.doApproachMotion({
        target: targets[0] as ArmTarget,
        targetSubstep: targetSubsteps[0],
        speedProfile: speedProfiles[0],
        totalCommonScaling: totalCommonScalings[0],
        pushUntilCollision: !!pushMode && !hasPushed,
        pushMode: !!pushMode,
        fail,
      });

      if (!approachCompleted) {
        return;
      }

      // The approach is where the first waypoint is actually reached, and
      // `targets` below drops its target, so nothing downstream would ever
      // report waypoint 1. The arm is stopped here, so unlike a crossing event
      // this waits for the waypoint's substeps.
      const waypointHandled = await this.handleWaypointReached(playArgs, {
        awaitSubsteps: true,
      });

      if (!waypointHandled) {
        return;
      }

      const shouldContinue = await this.handlePreflightPause(pauseRoutine);

      if (!shouldContinue) {
        return;
      }

      // Remove the approach target from arrays
      targets = targets.slice(1);
      targetSubsteps = targetSubsteps.slice(1);
      speedProfiles = speedProfiles.slice(1);
      totalCommonScalings = totalCommonScalings.slice(1);
      matchDynamicBasePositionArray = matchDynamicBasePositionArray.slice(1);

      // If there are no remaining targets after the approach (single-waypoint
      // move with constant speed), the step is complete.
      if (targets.length === 0) {
        info(
          ns`doPlay`,
          'No remaining targets after constant-speed approach; step complete',
        );

        this.stepDone = true;

        this.setVariable('completedCount', this.variables.completedCount + 1);

        return;
      }
    }

    const useCartesianPlanner = this.args.enforceConstantSpeed ?? false;

    // When using cartesian planner, build effective speed profiles by
    // overriding the tooltip speed/acceleration with the constant-speed values.
    // The joint speed/acceleration limits come from the step's own speed
    // profile (the single source of truth) — there is no separate
    // constant-speed limits override.
    const effectiveSpeedProfiles = useCartesianPlanner
      ? speedProfiles.map((sp) => {
          return {
            ...sp,
            ...(this.args.constantSpeedValue != null && {
              maxTooltipSpeed: this.args.constantSpeedValue,
            }),
            // Falls back to default when the user hasn't explicitly set
            // an acceleration value in the constant-speed UI.
            maxTooltipAcceleration:
              this.args.constantSpeedAcceleration ??
              DEFAULT_CONSTANT_SPEED_TOOLTIP_ACCELERATION,
          };
        })
      : speedProfiles;

    // Resolve tool frame for cartesian planning — must match the frame
    // used by the targets' poses, otherwise IK fails.
    // TCP offset option was already set at the top of doPlay so that
    // target collection uses the correct frame.
    let toolFrame: string | undefined;

    if (useCartesianPlanner && constantSpeedForcedFrame) {
      // Enabled AND disabled rows: a weld frame's id can name a torch that
      // is currently swapped out — the URDF keeps disabled grippers' links,
      // and only the id reaches them. Name-only frames still resolve against
      // enabled rows inside the resolver.
      const tcpResult = resolveTCPFrame(
        this.routineContext.allEquipmentItems,
        constantSpeedForcedFrame,
      );

      toolFrame = tcpResult.tcpLink ?? 'tool0';
    }

    // Yield to the macrotask queue before kicking off doMotion. The
    // preceding work (getArmTarget loop, monitoring setup, profile
    // building) and the following work (pushWaypoint x N, handler
    // registration x N, planning + ROS sendGoal) both land in the same
    // tick at a step boundary; breaking them apart lets the heartbeat
    // setInterval fire between them.
    await yieldToEventLoop();

    // Bail if `_pause` or `_stop` arrived during the yield. Both setters
    // flip their respective flags synchronously but their `this.motion`
    // dispatch is a no-op until the assignment below — without this
    // check the arm would start moving despite the routine being
    // paused or stopped. The outer `_play` loop handles the state.
    if (this.paused || this.stepDone) {
      this.stopMonitoringStopEarly();
      this.stopDistanceMonitoring();
      await this.stopHexEForceMonitoring();

      return;
    }

    const motion = this.routineContext.doMotion({
      request: {
        targets: targets as NonEmptyArray<ArmTarget>,
        useCartesianPlanner,
        toolFrame,
      },
      speedProfiles: effectiveSpeedProfiles,
      totalCommonScalings,
      pushUntilCollision: !!pushMode && !hasPushed,
      pushMode: !!pushMode,
      stepID: this.id,
      moveDynamicBaseToReachPosition: this.args.moveDynamicBaseToReachPosition,
      matchDynamicBasePositionArray,
      describeTarget: (targetIndex) => {
        const substep = targetSubsteps[targetIndex];

        return substep ? this.getMotionSubstepLabel(substep) : undefined;
      },
    });

    motion.on('requestingPlan', () => {
      if (this.variables.currentActivity === 'none') {
        this.setVariable('currentActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.currentActivity === 'requestingPlan') {
        this.setVariable('currentActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('currentActivity', 'moving');
    });

    motion.on('pause', () => {
      // If the pause was caused by our own speed pause very recently, ignore once
      if (this.isDeviceActuationInProgress() && this.lastSelfPauseAtMs) {
        const elapsed = Date.now() - this.lastSelfPauseAtMs;

        if (elapsed >= 0 && elapsed < 100) {
          this.lastSelfPauseAtMs = null;

          return;
        }
      }

      this.setVariable('currentActivity', 'paused');

      if (this.isDeviceActuationInProgress()) {
        // Apply pause to lift actuation, independent of routine pause
        this.deviceActuationPaused = true;
        (this.motion as any)?.emit?.('deviceActuationPaused');
        this.routineContext.stopDynamicBaseActuation();

        const rrState = this.routineContext.getRoutineRunnerState();

        if (rrState.kind === 'RoutineRunning') {
          this.routineContext.setRoutineRunnerState({
            ...rrState,
            deviceActuationState: { inProgress: true, paused: true },
          } as any);
        }
      }
    });

    motion.on('waypointReached', async (...eventData: unknown[]) => {
      // Defensive: eventData may be empty, so check before destructuring
      info(ns`doPlay`, `waypointReached`, eventData);
      const waypointIndex = eventData.length > 0 ? eventData[0] : undefined;

      if (typeof waypointIndex === 'number') {
        await this.handleWaypointReached(playArgs);
      }
    });

    // Handle initial device actuation after readyToBeginMotion
    motion.on(
      'initialDeviceActuationRequired',
      async (deviceCommand: DeviceCommand) => {
        try {
          await this.executeInitialDeviceActuation(deviceCommand);
          motion.emit('initialDeviceActuationCompleted');
        } catch (error) {
          info(ns`initialDeviceActuation`, 'Initial device actuation failed', {
            error,
          });

          motion.emit('initialDeviceActuationFailed');
        }
      },
    );

    motion.on(
      'deviceActuationRequired',
      async (deviceCommand: DeviceCommand) => {
        try {
          await this.handleDeviceActuationRequired(deviceCommand, motion, fail);
        } catch (err) {
          await this.handleDeviceActuationFailure(err, motion);
        }
      },
    );

    // If user presses Play while lift is still moving, keep arm paused but let lift resume
    motion.on('resume', async () => {
      if (this.isDeviceActuationInProgress()) {
        this.deviceActuationPaused = false;
        motion.emit('deviceActuationResumed');
        const rrState = this.routineContext.getRoutineRunnerState();

        if (rrState.kind === 'RoutineRunning') {
          this.routineContext.setRoutineRunnerState({
            ...rrState,
            deviceActuationState: { inProgress: true, paused: false },
          } as any);
        }
      }
    });

    // Handle preflight test run completion when first waypoint is reached
    motion.once('waypointReached', () => {
      if (targets.length !== 1) {
        this.checkPreflightComplete();
      }
    });

    motion.once('complete', () => {
      if (targets.length === 1) {
        this.checkPreflightComplete();
      }
    });

    motion.on('complete', () => {
      this.stepDone = true;

      this.setVariable('completedCount', this.variables.completedCount + 1);
    });

    motion.on('cancelled', () => {
      this.setVariable('currentActivity', 'none');
    });

    motion.on('failure', ({ failedTargetIndex, ...failure }) => {
      this.stepDone = true;

      fail({
        ...failure,
        failedSubstepId:
          failedTargetIndex != null
            ? targetSubsteps[failedTargetIndex]?.id
            : undefined,
      });
    });

    this.motion = motion;
    this.motionPromise = motion.race('failure', 'complete', 'cancelled');

    try {
      await this.motionPromise;
    } finally {
      // Clean up regardless of how the motion ended
      this.setVariable('currentActivity', 'none');
      motion.removeAllListeners();
      this.stopMonitoringStopEarly();
      this.stopDistanceMonitoring();
      await this.stopHexEForceMonitoring();

      delete this.motion;
      delete this.motionPromise;
    }
  }

  /**
   * Settle the tooltips this play cycle resolves against. Called once per cycle,
   * so the wrist-flange warning below says so once per cycle too.
   */
  private resolvePlayCycleTooltips(): PlayCycleTooltips {
    // Read BEFORE the constant-speed frame switch in `doPlay`, which is itself
    // derived from this value. A SEPARATE capture from
    // `capturedGlobalTCPOption`: that one is the name-only motion frame, while
    // this is the user-intent tooltip, both halves.
    //
    // Reading the field is not flag-gated: a tooltip already in the state keeps
    // its meaning. The fallback is, because it stands in for states that
    // predate `ConfigurationState.activeTooltip`. Flag-off nothing writes the
    // field, so an ungated fallback would name whichever frame the last motion
    // left behind.
    const { kinematicState, configuration } =
      this.routineContext.getRoutineRunnerState();

    const activeTooltip: TCPFrame | undefined =
      configuration.activeTooltip ??
      (getFeatureFlagSync('setActiveTooltip', false)
        ? { tcpOption: kinematicState.tcpOffsetOption }
        : undefined);

    // The Move step's own tooltip governs wherever it is stored;
    // `constantSpeedTCPOption` still governs the steps that predate it, and
    // neither field is rewritten.
    const stepTooltip = getEffectiveStepTooltip(this.args);

    // One continuous trajectory has one tooltip to hold a steady speed at, so
    // constant speed pins the frame on every substep rather than letting each
    // one name its own — the same pinning a Weld step applies with its torch.
    // Outside constant speed the same tooltip is offered for inheritance.
    const constantSpeedForcedFrame = this.args.enforceConstantSpeed
      ? resolveStepTooltip(stepTooltip, activeTooltip)
      : undefined;

    if (constantSpeedForcedFrame?.tcpOption === DEFAULT_TCP_OFFSET_OPTION) {
      // Constant speed at the bare flange is unusual enough to say so, but it
      // is a legitimate deliberate tooltip — the step runs.
      warn(
        ns`resolvePlayCycleTooltips`,
        'constant speed is measured at the wrist flange, not at a tooltip',
        { stepID: this.id },
      );
    }

    return { activeTooltip, stepTooltip, constantSpeedForcedFrame };
  }

  public _pause(): void {
    this.paused = true;

    if (this.motionApproach) {
      this.motionApproach.emit('pause');
    }

    if (this.motion) {
      this.motion.emit('pause');
    }
  }

  public _resume(): void {
    this.paused = false;

    this.resolvePauseGate('resume');

    if (this.motionApproach) {
      this.motionApproach.emit('resume');
    }

    if (this.motion) {
      this.motion.emit('resume');
    }

    // If we were in lift actuation mode, mimic MoveVerticalLift resume
    if (this.deviceActuationInProgress) {
      this.deviceActuationPaused = false;
      (this.motion as any)?.emit?.('deviceActuationResumed');
    }
  }

  public async _stop() {
    info(ns`_stop`, `stopping MoveArmToV2Step`);

    this.paused = false;

    this.stepDone = true;

    this.resolvePauseGate('stop');

    // Cancel any ongoing device actuation
    this.clearDeviceActuationState();

    if (this.motionApproach) {
      info(ns`_stop`, `cancelling approach motion`);
      this.motionApproach.emit('cancel');
    }

    if (this.motion) {
      info(ns`_stop`, `cancelling motion`);
      this.motion.emit('cancel');

      if (this.motionPromise) {
        info(ns`_stop`, 'waiting for motion to finish');
        await this.motionPromise;
        info(ns`_stop`, 'motion finished');
        // Ensure any speed pause is cleared after motion completes
        await this.routineContext.resumeMotionWithSpeed().catch(() => {});
      }
    }

    this.stopMonitoringStopEarly();
    await this.stopHexEForceMonitoring();
  }

  public hasSelfTimeout(): boolean {
    return false;
  }

  private createPauseGate(): void {
    if (this.pauseGate) return;

    let resolve!: (kind: 'resume' | 'stop') => void;

    const promise = new Promise<'resume' | 'stop'>((r) => {
      resolve = r;
    });

    this.pauseGate = { promise, resolve };
  }

  private async waitForResume(): Promise<'resume' | 'stop'> {
    this.createPauseGate();

    const kind = await this.pauseGate!.promise;
    this.pauseGate = null;

    return kind;
  }

  private resolvePauseGate(kind: 'resume' | 'stop'): void {
    if (!this.pauseGate) return;

    this.pauseGate.resolve(kind);
    this.pauseGate = null;
  }

  /**
   * After the approach motion completes, check if this is a preflight test run.
   * If so, pause the routine and block until the user resumes (or stops).
   * Returns true if execution should continue, false if stopped.
   */
  private async handlePreflightPause(
    pauseRoutine: (pauseDetails: any) => void,
  ): Promise<boolean> {
    info(ns`handlePreflightPause`, 'checking preflight completion');

    const routineRunnerState = this.routineContext.getRoutineRunnerState();

    if (
      routineRunnerState.kind === 'RoutineRunning' &&
      routineRunnerState.isPreflightTestRun
    ) {
      info(ns`handlePreflightPause`, 'preflight test run completed');

      this.routineContext.setRoutineRunnerState({
        ...routineRunnerState,
        isPreflightTestRun: false,
        readyToBeginMotion: true,
        shouldCheckGuidedMode: false,
        shouldNextArmMoveBeGuidedMode: false,
      });

      pauseRoutine({
        kind: 'preflightTestRunCompleted',
        reason: 'The jogging motion completed.',
      });

      const kind = await this.waitForResume();

      if (kind === 'stop') {
        return false;
      }

      return true;
    }

    info(ns`handlePreflightPause`, 'preflight is not active');

    return true;
  }

  /**
   * Do a normal (non-cartesian) joint move to the first waypoint.
   */
  private async doApproachMotion({
    target,
    targetSubstep,
    speedProfile,
    totalCommonScaling,
    pushUntilCollision,
    pushMode,
    fail,
  }: {
    target: ArmTarget;
    targetSubstep?: WaypointStep | ArcStep;
    speedProfile: SpeedProfile;
    totalCommonScaling: number;
    pushUntilCollision: boolean;
    pushMode: boolean;
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<boolean> {
    let completed = false;

    this.setVariable('currentActivity', 'approaching');

    const motion = this.routineContext.doMotion({
      request: {
        targets: [target] as NonEmptyArray<ArmTarget>,
      },
      speedProfiles: [speedProfile],
      totalCommonScalings: [totalCommonScaling],
      pushUntilCollision,
      pushMode,
      stepID: this.id,
      describeTarget: (targetIndex) =>
        targetIndex === 0 && targetSubstep
          ? this.getMotionSubstepLabel(targetSubstep)
          : undefined,
    });

    motion.on('requestingPlan', () => {
      if (this.variables.currentActivity === 'approaching') {
        this.setVariable('currentActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.currentActivity === 'requestingPlan') {
        this.setVariable('currentActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('currentActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('currentActivity', 'paused');
    });

    motion.on('resume', () => {
      this.setVariable('currentActivity', 'moving');
    });

    motion.on('complete', () => {
      info(ns`doApproachMotion`, 'Approach motion complete');
      completed = true;
    });

    motion.on('cancelled', () => {
      this.setVariable('currentActivity', 'none');
    });

    motion.on('failure', ({ failedTargetIndex, ...failure }) => {
      this.setVariable('currentActivity', 'none');

      fail({
        ...failure,
        failedSubstepId:
          failedTargetIndex === 0 ? targetSubstep?.id : undefined,
      });
    });

    this.motionApproach = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      this.setVariable('currentActivity', 'none');
      motion.removeAllListeners();
      delete this.motionApproach;
    }

    return completed;
  }

  private startMonitoringStopEarly() {
    const motionSubsteps = this.getActiveMotionSubsteps();

    const hasAnyForceCondition = motionSubsteps.some(
      (substep) =>
        this.isWaypointStep(substep) && this.hasForceCondition(substep),
    );

    if (hasAnyForceCondition) {
      info(
        ns`startMonitoringStopEarly`,
        `Has force condition, using fast interval`,
      );
    }

    const intervalMs = hasAnyForceCondition
      ? MoveArmToV2Step.STOP_EARLY_CHECK_INTERVAL_MS_FAST
      : MoveArmToV2Step.STOP_EARLY_CHECK_INTERVAL_MS_DEFAULT;

    // start interval to check for stop early conditions
    this.removeStopEarlyMonitoring = setInterval(async () => {
      await this.checkStopEarlyConditions();
    }, intervalMs);
  }

  private stopMonitoringStopEarly() {
    if (this.removeStopEarlyMonitoring) {
      clearInterval(this.removeStopEarlyMonitoring);
    }
  }

  /**
   * Enables HexE force monitoring for the current waypoint's threshold.
   * If the current waypoint has no force condition, monitoring is disabled.
   */
  private async updateHexEForceMonitoring() {
    const motionSubsteps = this.getActiveMotionSubsteps();

    const substep = motionSubsteps[this.currentWaypointIndex];

    // The force-condition test reads Waypoint arguments, so the gates below
    // use a narrowed binding while the threshold lookup keeps the wider
    // substep, which arcs carry stop-early conditions on too.
    const forceConditionWaypoint =
      substep !== undefined && this.isWaypointStep(substep)
        ? substep
        : undefined;

    // Only a condition arm-control-bot can threshold is subject to the
    // realtime rule. A virtual-sensor condition has no realtime monitor to
    // fall back from, so it stays on the poll and never reaches the throws
    // below.
    const wantsForceStop =
      forceConditionWaypoint !== undefined &&
      forceConditionWaypoint.hasArmableForceCondition();

    // Operators read these lines against the routine editor, which numbers
    // motion substeps from one.
    const substepLabel = substep
      ? this.getMotionSubstepLabel(substep)
      : `waypoint ${this.currentWaypointIndex + 1}`;

    // Simulation keeps the polling fallback. There is no physical arm to drive
    // into a workpiece, and the HexE simulator maintains a real force
    // magnitude, so the poll still evaluates the condition — what is lost is
    // timing fidelity, not the check.
    if (this.routineContext.robot.robotKind !== 'live') {
      if (wantsForceStop) {
        info(
          ns`updateHexEForceMonitoring`,
          `Force stop-early condition on ${substepLabel} falls back to polling: robotKind is '${this.routineContext.robot.robotKind}' rather than 'live'.`,
        );
      }

      return;
    }

    // Nothing to arm for this segment. Releasing a monitor no step left armed
    // is what put an RPC on every waypoint of every move.
    if (!wantsForceStop) {
      await this.disarmForceMonitor();

      return;
    }

    // Past this point the waypoint carries a force stop-early condition, and a
    // force stop is realtime or nothing. Every route that cannot arm
    // arm-control-bot's monitor throws, and both call sites fail the step:
    // a force stop running at poll speed looks like it works and does not.
    const requiresRealtime =
      'Force stop-early requires the arm-control-bot realtime force monitor.';

    if (!getFeatureFlagSync('forceTorqueSensor', false)) {
      throw new Error(
        `Cannot arm force stop-early on ${substepLabel}. The forceTorqueSensor feature flag is off, so the force/torque device never subscribes and the force reads zero. ${requiresRealtime}`,
      );
    }

    if (!this.routineContext.forceTorqueConnector) {
      throw new Error(
        `Cannot arm force stop-early on ${substepLabel}. No force/torque sensor is configured on this robot. ${requiresRealtime}`,
      );
    }

    const threshold = this.getHexEForceMagnitudeThreshold(
      substep as WaypointStep,
    );

    if (threshold === null) {
      throw new Error(
        `Cannot arm force stop-early on ${substepLabel}. No force magnitude threshold could be read from the condition. It must compare a force magnitude variable with '>' against a numeric constant. ${requiresRealtime}`,
      );
    }

    try {
      await this.routineContext.forceTorqueConnector.SetForceTorqueMonitor({
        enabled: true,
        forceMagnitudeThresholdN: threshold,
      });
    } catch (err) {
      throw new Error(
        `Cannot arm force stop-early on ${substepLabel}. Arm-control-bot did not accept the force monitor request: ${
          (err as Error)?.message ?? err
        }. ${requiresRealtime}`,
      );
    }

    this.routineContext.forceMonitorArmed = true;

    info(
      ns`updateHexEForceMonitoring`,
      `Enabled HexE force monitoring with threshold: ${threshold}N for ${substepLabel}`,
    );
  }

  /**
   * Releases the realtime monitor, but only when a step left it armed. Letting
   * a failure here fail the step would turn a teardown detail into a routine
   * failure, so it is logged instead.
   */
  private async disarmForceMonitor() {
    if (!this.routineContext.forceMonitorArmed) {
      return;
    }

    const connector = this.routineContext.forceTorqueConnector;

    if (!connector) {
      this.routineContext.forceMonitorArmed = false;

      return;
    }

    try {
      await connector.SetForceTorqueMonitor({
        enabled: false,
        forceMagnitudeThresholdN: 0,
      });

      this.routineContext.forceMonitorArmed = false;

      info(ns`disarmForceMonitor`, 'Disabled HexE force monitoring');
    } catch (error) {
      warn(
        ns`disarmForceMonitor`,
        `Failed to disable HexE force monitoring: ${error}`,
      );
    }
  }

  private async stopHexEForceMonitoring() {
    await this.disarmForceMonitor();
  }

  private async checkStopEarlyConditions() {
    const waypointSteps = this.getActiveMotionSubsteps();
    const substep = waypointSteps[this.currentWaypointIndex] as WaypointStep;

    if (!substep || !substep.hasStopEarlyConditions()) {
      return false;
    }

    let conditionMet: boolean;

    try {
      conditionMet = await substep.evaluateStopEarlyCondition();
    } catch (err) {
      // This runs on an interval, so an escaping rejection would be unhandled
      // and the arm would keep moving. A force condition that cannot be
      // evaluated fails the step instead.
      this.stopMonitoringStopEarly();

      const reason =
        (err as Error)?.message ??
        'Stop-early condition could not be evaluated';

      if (this.failCallback) {
        await this.failCallback({
          failure: { kind: FailureKind.ExecutionFailure },
          failureReason: reason,
          error: err as Error,
        });
      } else {
        warn(ns`checkStopEarlyConditions`, reason);
      }

      return false;
    }

    if (conditionMet) {
      info(ns`startMonitoringStopEarly`, 'stop early condition met');

      // Handle preflight test run pause BEFORE incrementing/cancelling
      // This ensures the pause happens before we move to the next waypoint
      this.checkPreflightComplete();

      // Cancel whichever motion is live. During the constant-speed approach
      // that is `motionApproach`; leaving it running while the index moves on
      // lets its completion do the *next* waypoint's bookkeeping.
      this.motionApproach?.emit('cancel');
      this.motion?.emit('cancel');

      await this.incrementWaypointIndex();

      return true;
    }

    return false;
  }

  /**
   * Bookkeeping for a waypoint the arm has just arrived at: play the
   * non-motion substeps authored after it, advance the waypoint index, and
   * re-arm force monitoring for the waypoint now being moved toward.
   *
   * Both arrival paths run this — the main motion's `waypointReached`
   * crossings, and the constant-speed approach, a separate single-target
   * motion whose completion is the first waypoint's arrival.
   *
   * `awaitSubsteps` is true only for the approach, where the arm is stopped. A
   * crossing handler is mid-blend and must return promptly, so it leaves the
   * substeps detached and always gets true back; false means a substep failed
   * or a stop landed part-way, and the caller must not start the next motion.
   *
   * A waypoint the arm stops *short* of is not an arrival, so
   * `checkStopEarlyConditions` only advances the index. Whether a cut-short
   * segment should fire its trailing substeps is an open product question.
   */
  private async handleWaypointReached(
    playArgs: StepPlayArguments,
    { awaitSubsteps = false }: { awaitSubsteps?: boolean } = {},
  ): Promise<boolean> {
    const justReachedIndex = this.currentWaypointIndex;

    const substeps = this.playSubstepsAfterWaypoint(playArgs, justReachedIndex);
    let substepsCompleted = true;

    if (awaitSubsteps) {
      substepsCompleted = await substeps;
    } else {
      const justReachedSubstep =
        this.getActiveMotionSubsteps()[justReachedIndex];

      const justReachedLabel = justReachedSubstep
        ? this.getMotionSubstepLabel(justReachedSubstep)
        : `waypoint ${justReachedIndex + 1}`;

      substeps.catch((err) => {
        warn(
          ns`handleWaypointReached`,
          `Substeps after ${justReachedLabel} threw: ${err}`,
        );
      });
    }

    await this.incrementWaypointIndex();

    // Awaited and failed here, exactly as at the start-of-move call site. A
    // warn would let the first waypoint fail loudly while every later one
    // degraded in silence.
    try {
      await this.updateHexEForceMonitoring();
    } catch (err) {
      await playArgs.fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason:
          (err as Error)?.message ??
          'Failed to arm force stop-early monitoring',
        error: err as any,
      });

      return false;
    }

    info(
      ns`handleWaypointReached`,
      `waypoint reached, set currentWaypointIndex to ${this.currentWaypointIndex}`,
    );

    return substepsCompleted;
  }

  /**
   * Play the non-motion substeps authored between `waypointIndex` and the next
   * motion substep, in order. Resolves false if one failed or a stop landed
   * part-way through.
   */
  private async playSubstepsAfterWaypoint(
    playArgs: StepPlayArguments,
    waypointIndex: number,
  ): Promise<boolean> {
    const { fail } = playArgs;

    for (const step of this.getActiveNonMotionSubstepsBetweenWaypoints(
      waypointIndex,
    )) {
      // Nothing else aborts this chain, so without this the remaining
      // substeps keep actuating IO and grippers after `_stop` has resolved.
      if (this.stepDone) {
        info(
          ns`playSubstepsAfterWaypoint`,
          `step stopped, skipping remaining substeps after waypoint ${waypointIndex}`,
        );

        return false;
      }

      try {
        await step.play(playArgs);
      } catch (err) {
        await fail({
          failure: { kind: FailureKind.ExecutionFailure },
          failureReason:
            (err as Error)?.message ?? 'Non-waypoint substep failed',
          error: err as any,
        });

        return false;
      }
    }

    return true;
  }

  private async incrementWaypointIndex() {
    this.currentWaypointIndex += 1;

    if (this.currentWaypointIndex >= this.getActiveMotionSubsteps().length) {
      this.stepDone = true;
    }

    this.publishActiveWaypoint();

    await this.checkStopEarlyConditions();
  }

  /**
   * Publish the step ID of the motion substep the arm is currently moving
   * toward, so step-list views can highlight the active waypoint. Publishes
   * null when the index has run past the last active motion substep.
   */
  private publishActiveWaypoint(): void {
    const substep = this.getActiveMotionSubsteps()[this.currentWaypointIndex];

    this.setVariable('currentWaypointID', substep?.id ?? null);
  }

  private startDistanceMonitoring() {
    if (this.distanceMonitoringHandle) {
      return;
    }

    info(
      ns`distanceMonitoring`,
      `About to start monitoring - currentActivity: ${this.variables.currentActivity}, currentWaypointStartingPose: ${this.currentWaypointStartingPose}`,
    );

    // Start monitoring distance from starting pose during motion execution
    this.distanceMonitoringHandle = this.startMonitoring(
      () => {
        const currentPose =
          this.routineContext.getRoutineRunnerState().kinematicState
            .tooltipPoint;

        const startingPose = this.currentWaypointStartingPose;

        if (!startingPose) {
          throw new Error('Starting pose is not set');
        }

        const distance = distanceBetweenPoses(startingPose, currentPose);

        this.setVariable('distanceMovedToNextWaypoint', distance);

        return { triggered: false }; // Continue monitoring
      },
      {
        onError: (err) => {
          if (this.failCallback) {
            this.failCallback({
              failure: { kind: FailureKind.InternalFailure },
              failureReason: 'Starting pose is not set',
              error: err as Error,
            });
          } else {
            throw err;
          }
        },
      },
      { pollIntervalMs: 100 },
    );
  }

  private stopDistanceMonitoring() {
    if (this.distanceMonitoringHandle) {
      this.distanceMonitoringHandle();
      this.distanceMonitoringHandle = null;
      this.setVariable('distanceMovedToNextWaypoint', 0);
      this.currentWaypointStartingPose = null;
    }
  }

  // Wait for lift/device movement completion using equipment state polling
  private async waitForDeviceMovementCompletion(
    deviceCommand: DeviceCommand,
  ): Promise<void> {
    if (deviceCommand.kind === 'EwellixLiftTLTCommand') {
      const targetHeight = deviceCommand.heightMeters;
      const STRICT_THRESHOLD_DIST = 0.002; // 2mm
      const maxWaitTime = 30000;
      const pollInterval = 100;
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitTime) {
        // Exit early if routine/step is stopping
        if (await this.shouldStopPlaying()) {
          return;
        }

        const deviceState = this.routineContext.equipment
          .getEquipmentState()
          .find((item) => item.kind === 'EwellixLiftTLT');

        if (
          deviceState &&
          'state' in deviceState &&
          'heightMeters' in (deviceState as any).state
        ) {
          const currentHeight = (deviceState as any).state
            .heightMeters as number;

          const heightDifference = Math.abs(currentHeight - targetHeight);

          if (heightDifference <= STRICT_THRESHOLD_DIST) {
            return;
          }
        }

        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }
    } else {
      const maxWaitTime = 10000;
      const pollInterval = 100;
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitTime) {
        if (await this.shouldStopPlaying()) {
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }
    }
  }

  /**
   * Execute initial device actuation after readyToBeginMotion
   * This handles device movements that need to happen before the arm starts moving
   * Uses the same pause/resume pattern as mid-move device actuation
   */
  public async executeInitialDeviceActuation(
    deviceCommand: DeviceCommand,
  ): Promise<void> {
    try {
      info(ns`initialDeviceActuation`, 'Executing initial device movement', {
        deviceCommand,
      });

      // Mark actuation active before any awaits to avoid race with pause
      this.setDeviceActuationState(true);
      this.deviceActuationPaused = false;

      // Surface device actuation state to UI without setting isPaused
      const rrState = this.routineContext.getRoutineRunnerState();

      if (rrState.kind === 'RoutineRunning') {
        this.routineContext.setRoutineRunnerState({
          ...rrState,
          deviceActuationState: { inProgress: true, paused: false },
        } as any);
      }

      // Use the same retry loop pattern as mid-move device actuation
      while (this.deviceActuationInProgress) {
        // Check if step should stop before each iteration
        if (await this.shouldStopPlaying()) {
          info(
            ns`initialDeviceActuation`,
            'Step stopped, cancelling initial device actuation',
          );

          return;
        }

        try {
          // Execute the device command
          await this.routineContext.actuateDevice(deviceCommand);
          info(ns`initialDeviceActuation`, 'actuation call resolved');
        } catch (error) {
          // If paused during actuation, continue loop and retry after resume
          if (this.deviceActuationPaused) {
            info(ns`initialDeviceActuation`, 'actuation interrupted by pause');
            continue;
          }

          throw error;
        }

        // If not paused, we reached the target or stop was requested; break to finalize
        if (!this.deviceActuationPaused) {
          break;
        }

        // Wait for resume before retrying
        while (this.deviceActuationPaused) {
          // Check for stop condition even while waiting for resume
          if (await this.shouldStopPlaying()) {
            info(
              ns`initialDeviceActuation`,
              'Step stopped while waiting for resume',
            );

            return;
          }

          await new Promise((r) => setTimeout(r, 100));
        }
      }

      // Finalize: wait until device reaches the requested state, then recalibrate
      await this.waitForDeviceMovementCompletion(deviceCommand);
      info(ns`initialDeviceActuation`, 'device movement completion confirmed');

      // Emit recalibration event to clear pose cache and prevent duplicate targets
      this.motion?.emit('recalibrateAfterDeviceMovement');

      // Clear device actuation state
      this.setDeviceActuationState(false);
      const endState = this.routineContext.getRoutineRunnerState();

      if (endState.kind === 'RoutineRunning') {
        this.routineContext.setRoutineRunnerState({
          ...endState,
          deviceActuationState: { inProgress: false, paused: false },
        } as any);
      }

      info(ns`initialDeviceActuation`, 'Initial device movement completed');
    } catch (error) {
      this.setDeviceActuationState(false);

      info(ns`initialDeviceActuation`, 'Initial device actuation failed', {
        error,
      });

      throw error;
    }
  }

  private isWaypointStep(step: Step<any, any>): step is WaypointStep {
    return step.getStepKind() === 'Waypoint';
  }

  private getScalingFromWptSpeedLimits(step: WaypointStep | ArcStep): number {
    if (!getFeatureFlagSync('waypointTimingScaling', true)) {
      return 1;
    }

    if (!this.isWaypointStep(step)) {
      return 1;
    }

    if (step.args.useParentSpeedProfile) {
      return 1;
    }

    const percent = step.args.motionSpeedPercent ?? 100;

    const clampedPercent = Math.min(Math.max(percent, 1), 100);

    return clampedPercent / 100;
  }

  /**
   * Checks if a waypoint step has a force stop early condition.
   * A force condition is one that references virtualForceTorqueSensorWrench in its path.
   */
  private hasForceCondition(waypointStep: WaypointStep): boolean {
    return waypointStep.hasForceCondition();
  }

  /**
   * Finds and extracts the HexE force magnitude threshold from a waypoint's
   * stop-early condition tree. Returns the numeric threshold if a
   * `gripperState.forceMagnitude > <constant>` condition is found anywhere
   * in the expression tree, or null if the waypoint has no such condition.
   */
  private getHexEForceMagnitudeThreshold(
    waypointStep: WaypointStep,
  ): number | null {
    if (!waypointStep.hasStopEarlyConditions()) {
      return null;
    }

    const { stopEarlyConditions } = waypointStep.args;

    if (!stopEarlyConditions) {
      return null;
    }

    const isHexEForceExpression = (expression: Expression): boolean => {
      if (
        expression.kind === 'stateVariable' &&
        expression.path.includes('gripperState.forceMagnitude')
      ) {
        return true;
      }

      return (
        expression.kind === 'equipment' && expression.path === 'forceMagnitude'
      );
    };

    const extractFromCondition = (condition: Conditional): number | null => {
      if (
        condition.operator === '>' &&
        isHexEForceExpression(condition.leftOperand) &&
        condition.rightOperand.kind === 'constant' &&
        typeof condition.rightOperand.value === 'number'
      ) {
        return condition.rightOperand.value;
      }

      // Recurse into nested conditionals on either side
      for (const operand of [condition.leftOperand, condition.rightOperand]) {
        if (operand.kind === 'conditional') {
          const result = extractFromCondition(operand as Conditional);

          if (result !== null) return result;
        }
      }

      return null;
    };

    return extractFromCondition(stopEarlyConditions);
  }

  /**
   * Returns the unsuppressed substeps that contribute motion targets (Waypoints and Arcs).
   * Suppressed waypoints/arcs are excluded so the robot does not move through them.
   */
  private getActiveMotionSubsteps(): Array<WaypointStep | ArcStep> {
    return this.getActiveMotionSubstepsWithIndex().map((m) => m.step);
  }

  /**
   * Human-readable label for a motion substep, e.g. "waypoint 3" or
   * "arc 2 ('Around fixture')". The ordinal counts all Waypoint/Arc substeps
   * (including suppressed ones) so it matches the order shown in the routine
   * editor.
   */
  private getMotionSubstepLabel(substep: WaypointStep | ArcStep): string {
    const allMotionSubsteps = (this.substeps ?? []).filter((s) => {
      const kind = s.getStepKind?.();

      return kind === 'Waypoint' || kind === 'Arc';
    });

    const position = allMotionSubsteps.indexOf(substep);
    const kindLabel = substep.getStepKind?.() === 'Arc' ? 'arc' : 'waypoint';
    const ordinal = position >= 0 ? ` ${position + 1}` : '';
    const description = substep.description?.trim();

    return description
      ? `${kindLabel}${ordinal} ('${description}')`
      : `${kindLabel}${ordinal}`;
  }

  /**
   * Returns unsuppressed motion substeps (Waypoint/Arc) with their index in the full substeps array.
   * Suppressed waypoints/arcs are excluded — the parent MoveArmV2 calls getArmTarget()
   * directly rather than going through RoutineRunner's play() suppression check,
   * so filtering must happen here.
   */
  private getActiveMotionSubstepsWithIndex(): Array<{
    step: WaypointStep | ArcStep;
    index: number;
    kind: 'Waypoint' | 'Arc';
  }> {
    const all = this.substeps ?? [];

    const result: Array<{
      step: WaypointStep | ArcStep;
      index: number;
      kind: 'Waypoint' | 'Arc';
    }> = [];

    for (let i = 0; i < all.length; i += 1) {
      const s = all[i];
      const kind = s.getStepKind?.();

      if (kind === 'Waypoint' || kind === 'Arc') {
        if (s.isEffectivelySuppressed()) {
          continue;
        }

        result.push({ step: s as WaypointStep | ArcStep, index: i, kind });
      }
    }

    return result;
  }

  /**
   * Returns unsuppressed non-waypoint substeps located between waypoint[i] and waypoint[i+1].
   * If i is the last waypoint, returns all substeps after that waypoint.
   * Suppressed substeps are excluded — these are played directly by MoveArmV2
   * rather than going through RoutineRunner's play() suppression check.
   */
  private getActiveNonMotionSubstepsBetweenWaypoints(
    i: number,
  ): Array<Step<object, object>> {
    const all = this.substeps ?? [];
    const motionSubsteps = this.getActiveMotionSubstepsWithIndex();

    if (i < 0 || i >= motionSubsteps.length) return [];

    const startIdx = motionSubsteps[i].index + 1;
    const endIdx = motionSubsteps[i + 1]?.index ?? all.length; // non-inclusive

    return all.slice(startIdx, endIdx).filter((s) => {
      const kind = s.getStepKind?.();

      return (
        !(kind === 'Waypoint' || kind === 'Arc') && !s.isEffectivelySuppressed()
      );
    });
  }

  /**
   * Handles the main device actuation logic for mid-move device movements
   */
  private async handleDeviceActuationRequired(
    deviceCommand: DeviceCommand,
    motion: ReturnType<RoutineContext['doMotion']>,
    _fail: (failure: any) => Promise<void>,
  ): Promise<void> {
    info(ns`deviceActuation`, 'deviceActuationRequired received', {
      deviceCommand,
    });

    this.initializeDeviceActuationState();
    await this.pauseArmForDeviceActuation(motion);
    this.updateDeviceActuationUIState(true, false);

    await this.executeDeviceActuationWithRetry(deviceCommand);
    await this.finalizeDeviceActuation(deviceCommand, motion);
  }

  /**
   * Handles device actuation failure cleanup
   */
  private async handleDeviceActuationFailure(
    err: any,
    motion: ReturnType<RoutineContext['doMotion']>,
  ): Promise<void> {
    await this.routineContext.resumeMotionWithSpeed().catch(() => {});
    this.setDeviceActuationState(false);
    info(ns`deviceActuation`, 'actuation failed', { err });

    // The motion's failure handler releases the queue guard and calls fail().
    motion.emit('failure', {
      failure: { kind: FailureKind.ExecutionFailure },
      failureReason:
        (err as Error)?.message ?? 'Device actuation during waypoint failed',
      error: err as any,
    });
  }

  /**
   * Initialize device actuation state variables
   */
  private initializeDeviceActuationState(): void {
    this.setDeviceActuationState(true);
    this.deviceActuationPaused = false;
  }

  /**
   * Pause arm motion to keep it stationary during device actuation
   */
  private async pauseArmForDeviceActuation(
    motion: ReturnType<RoutineContext['doMotion']>,
  ): Promise<void> {
    this.lastSelfPauseAtMs = Date.now();
    await this.routineContext.pauseMotionWithSpeed();
    motion.emit('deviceActuationBegin');
  }

  /**
   * Update UI state to reflect device actuation status
   */
  private updateDeviceActuationUIState(
    inProgress: boolean,
    paused: boolean,
  ): void {
    const rrState = this.routineContext.getRoutineRunnerState();

    if (rrState.kind === 'RoutineRunning') {
      this.routineContext.setRoutineRunnerState({
        ...rrState,
        deviceActuationState: { inProgress, paused },
      } as any);
    }
  }

  /**
   * Execute device actuation with pause/resume retry logic
   */
  private async executeDeviceActuationWithRetry(
    deviceCommand: DeviceCommand,
  ): Promise<void> {
    while (this.deviceActuationInProgress) {
      // Check if step should stop before each iteration
      if (await this.shouldStopPlaying()) {
        info(ns`deviceActuation`, 'Step stopped, cancelling device actuation');

        return;
      }

      if (!this.deviceActuationPaused) {
        try {
          info(ns`deviceActuation`, 'actuating device');
          await this.routineContext.actuateDevice(deviceCommand);
          info(ns`deviceActuation`, 'actuation call resolved');
        } catch (error) {
          // If paused during actuation, continue loop and retry after resume
          if (this.deviceActuationPaused) {
            info(ns`deviceActuation`, 'actuation interrupted by pause');
            continue;
          }

          throw error;
        }

        // If not paused, we reached the target or stop was requested; break to finalize
        if (!this.deviceActuationPaused) {
          break;
        }
      }

      // Wait for resume before retrying
      while (this.deviceActuationPaused) {
        // Check for stop condition even while waiting for resume
        if (await this.shouldStopPlaying()) {
          info(ns`deviceActuation`, 'Step stopped while waiting for resume');

          return;
        }

        await new Promise((r) => setTimeout(r, 100));
      }
    }
  }

  /**
   * Finalize device actuation and handle cleanup
   */
  private async finalizeDeviceActuation(
    deviceCommand: DeviceCommand,
    motion: ReturnType<RoutineContext['doMotion']>,
  ): Promise<void> {
    // Wait until device reaches the requested state, then recalibrate
    await this.waitForDeviceMovementCompletion(deviceCommand);
    info(ns`deviceActuation`, 'device movement completion confirmed');
    motion.emit('recalibrateAfterDeviceMovement');

    // Do not auto-resume arm by default
    this.setDeviceActuationState(false);

    await this.handleDeviceActuationCompletion();

    // Must follow the resume: draining can start the next device move.
    motion.emit('deviceActuationEnd');
  }

  /**
   * Handle device actuation completion and arm resumption logic
   */
  private async handleDeviceActuationCompletion(): Promise<void> {
    const endState = this.routineContext.getRoutineRunnerState();

    if (endState.kind === 'RoutineRunning') {
      // Check if there are any remaining pause reasons after clearing deviceActuation
      const hasRemainingPauseReasons = Array.isArray(endState.pauseDetails)
        ? endState.pauseDetails.length > 0
        : false;

      this.routineContext.setRoutineRunnerState({
        ...endState,
        deviceActuationState: { inProgress: false, paused: false },
        // Update isPaused to match whether there are remaining pause reasons
        isPaused: hasRemainingPauseReasons,
      } as any);

      // If no remaining pause reasons, auto-resume arm speed (only for mid-move actuation)
      if (!hasRemainingPauseReasons) {
        await this.routineContext.resumeMotionWithSpeed().catch(() => {});
      }
    }
  }

  /**
   * Set device actuation state
   * Since only one device actuation occurs at a time, we only need one variable
   */
  private setDeviceActuationState(inProgress: boolean): void {
    this.deviceActuationInProgress = inProgress;
  }

  /**
   * Check if device actuation is currently in progress
   */
  private isDeviceActuationInProgress(): boolean {
    return this.deviceActuationInProgress;
  }

  /**
   * Clear device actuation state
   */
  private clearDeviceActuationState(): void {
    this.deviceActuationInProgress = false;
  }

  /**
   * Returns unsuppressed non-motion substeps that appear before the first motion substep.
   * These should be executed before planning begins.
   * Suppressed substeps are excluded — these are played directly by MoveArmV2
   * rather than going through RoutineRunner's play() suppression check.
   */
  private getActiveNonMotionSubstepsBeforeFirstMotion(): Array<
    Step<object, object>
  > {
    const all = this.substeps ?? [];
    const motionSubsteps = this.getActiveMotionSubstepsWithIndex();

    const firstMotionIndex = motionSubsteps[0]?.index ?? all.length;

    return all.slice(0, firstMotionIndex).filter((s) => {
      const kind = s.getStepKind?.();

      return (
        !(kind === 'Waypoint' || kind === 'Arc') && !s.isEffectivelySuppressed()
      );
    });
  }

  /**
   * Handle preflight test run completion - pause the routine if in preflight mode
   * Returns true if preflight was active and pause was triggered
   */
  private checkPreflightComplete(): boolean {
    info(ns`checkPreflightComplete`, 'checking preflight completion');

    const routineRunnerState = this.routineContext.getRoutineRunnerState();

    if (
      routineRunnerState.kind === 'RoutineRunning' &&
      routineRunnerState.isPreflightTestRun
    ) {
      info(ns`checkPreflightComplete`, 'preflight test run completed');

      // Set isPreflightTestRun to false
      this.routineContext.setRoutineRunnerState({
        ...routineRunnerState,
        isPreflightTestRun: false,
        readyToBeginMotion: true,
        shouldCheckGuidedMode: false,
        shouldNextArmMoveBeGuidedMode: false,
      });

      // Trigger pause for preflight test run completion
      if (this.pauseRoutineCallback) {
        this.pauseRoutineCallback({
          kind: 'preflightTestRunCompleted',
          reason: 'The jogging motion completed.',
        });
      }

      return true;
    }

    info(ns`checkPreflightComplete`, 'preflight is not active');

    return false;
  }
}
